import axios from 'axios';

// Student Activity Tracking Utility
class StudentTracking {
  constructor() {
    this.apiUrl = process.env.REACT_APP_API_URL;
    this.token = null;
    this.userId = null;
    this.userName = null;
    this.userRole = 'student';
  }

  // Initialize tracking with user data
  initialize(user, token) {
    this.token = token;
    this.userId = user?.studentId || user?.id;
    this.userName = user?.name || user?.email || 'Unknown';
  }

  // Track student activity
  async trackActivity(action, details = {}, metadata = {}) {
    if (!this.token || !this.userId) {
      console.warn('Student tracking not initialized');
      return;
    }

    try {
      const activityData = {
        userId: this.userId,
        userName: this.userName,
        userRole: this.userRole,
        action,
        details: JSON.stringify(details),
        metadata: JSON.stringify(metadata),
        timestamp: new Date().toISOString(),
        ipAddress: 'client-side', // Will be updated by backend
        sessionId: this.getSessionId(),
        pageUrl: window.location.href,
        userAgent: navigator.userAgent
      };

      // Send to backend tracking endpoint
      await axios.post(`${this.apiUrl}/api/student-tracking/track-student-activity`, activityData, {
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Activity tracked:', action, details);
    } catch (error) {
      console.error('Error tracking activity:', error);
      // Don't throw error to avoid breaking user experience
    }
  }

  // Get or create session ID
  getSessionId() {
    let sessionId = sessionStorage.getItem('student_session_id');
    if (!sessionId) {
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('student_session_id', sessionId);
    }
    return sessionId;
  }

  // Track page views
  trackPageView(pageName, additionalData = {}) {
    this.trackActivity('page_view', {
      page: pageName,
      ...additionalData
    });
  }

  // Track form interactions
  trackFormInteraction(formName, action, fieldName = null, value = null) {
    this.trackActivity('form_interaction', {
      form: formName,
      action, // 'focus', 'blur', 'change', 'submit', 'reset'
      field: fieldName,
      value: value ? (typeof value === 'string' ? value.substring(0, 100) : value) : null // Truncate long values
    });
  }

  // Track button clicks
  trackButtonClick(buttonName, context = {}) {
    this.trackActivity('button_click', {
      button: buttonName,
      ...context
    });
  }

  // Track data operations
  trackDataOperation(operation, entityType, entityId = null, details = {}) {
    this.trackActivity('data_operation', {
      operation, // 'create', 'read', 'update', 'delete'
      entityType, // 'patient', 'appointment', 'procedure_request', etc.
      entityId,
      ...details
    });
  }

  // Track search activities
  trackSearch(searchTerm, resultsCount = null, filters = {}) {
    this.trackActivity('search', {
      term: searchTerm,
      resultsCount,
      filters: JSON.stringify(filters)
    });
  }

  // Track file operations
  trackFileOperation(operation, fileType, fileName = null, fileSize = null) {
    this.trackActivity('file_operation', {
      operation, // 'upload', 'download', 'view', 'delete'
      fileType,
      fileName,
      fileSize
    });
  }

  // Track navigation
  trackNavigation(fromPage, toPage, method = 'click') {
    this.trackActivity('navigation', {
      from: fromPage,
      to: toPage,
      method
    });
  }

  // Dean-focused: Track explicit record access (audit)
  trackRecordAccess(entityType, entityId, purpose = 'view', context = {}) {
    this.trackActivity('record_access', {
      entityType,
      entityId,
      purpose,
      ...context
    });
  }

  // Dean-focused: Track auth/session events (login/logout/session_active)
  trackAuthEvent(eventType, context = {}) {
    this.trackActivity('auth_event', {
      eventType,
      ...context
    });
  }

  // Dean-focused: Track security-relevant events/suspicions
  trackSecurityEvent(eventType, context = {}) {
    this.trackActivity('security_event', {
      eventType,
      ...context
    });
  }

  // Track errors
  trackError(errorType, errorMessage, context = {}) {
    this.trackActivity('error', {
      type: errorType,
      message: errorMessage,
      ...context
    });
  }

  // Track time spent on page
  trackTimeOnPage(pageName, timeSpent) {
    this.trackActivity('time_on_page', {
      page: pageName,
      timeSpent: Math.round(timeSpent / 1000) // Convert to seconds
    });
  }

  // Track modal interactions
  trackModalInteraction(modalName, action, context = {}) {
    this.trackActivity('modal_interaction', {
      modal: modalName,
      action, // 'open', 'close', 'submit', 'cancel'
      ...context
    });
  }

  // Track API calls
  trackApiCall(endpoint, method, statusCode, responseTime = null) {
    this.trackActivity('api_call', {
      endpoint,
      method,
      statusCode,
      responseTime
    });
  }
}

// Create singleton instance
const studentTracking = new StudentTracking();

export default studentTracking;
